<template>
    <div class="animated fadeIn">
        <kgr-form-scaffold
            :with-back-button="false"
            :pending="loading"
            :save-button-disabled="!allValid"
            with-primary-buttons
            @primaryBtnClicked="save"
        >
            <div class="kgr-postfix kgr-postfix__fluid">
                <div class="kgr-inputGroup">
                    <div class="kgr-inputGroup__label" v-t="$t('Jahr')"></div>
                    <kgr-generic-select
                        v-if="years.length > 0"
                        :options="years"
                        :model-value="currentYear"
                        display="title"
                        no-null-option
                        @input="changeYear"
                    />
                </div>
            </div>
            <br />
            <kgr-table
                :is-data-table="false"
                style="width: 100%"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Art') }}*
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Beginn') }}*
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Ende') }}*
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Aktion') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(closingTime, i) in filteredClosingTimes"
                        :key="i"
                        class="kgr-table__row"
                        :class="{ deleted: closingTime.deleted }"
                    >
                        <td class="kgr-table__cell">
                            <kgr-generic-select
                                v-if="closingTime._edit"
                                :options="typeOptions"
                                :model-value="closingTime._edit.type"
                                display="text"
                                @input="closingTime._edit.type = $event ? $event.value : null; validate(closingTime)"
                                :error="closingTime._edit.error.messageForField('type')"
                            />
                            <span v-else>{{ typeOptions.find(o => o.value === closingTime.type).text }}</span>
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-date-picker
                                v-if="closingTime._edit"
                                v-model="closingTime._edit.start"
                                :error="closingTime._edit.error.messageForField('start')"
                                @post-input="validate(closingTime)"
                                @error="closingTime._edit.start = null;validate(closingTime)"
                                :min-date="currentYear.start"
                                :max-date="currentYear.end"
                            />
                            <span v-else>{{ closingTime.start }}</span>
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-date-picker
                                v-if="closingTime._edit"
                                v-model="closingTime._edit.end"
                                :error="closingTime._edit.error.messageForField('end')"
                                @post-input="validate(closingTime)"
                                @error="closingTime._edit.end = null;validate(closingTime)"
                                :min-date="currentYear.start"
                                :max-date="currentYear.end"
                            />
                            <span v-else>{{ closingTime.end }}</span>
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions">
                            <button
                                v-if="!closingTime._edit && !closingTime.deleted"
                                type="button"
                                class="kgr-table__action"
                                @click="edit(closingTime)"
                            >
                                <kgr-icon-edit :title="$t('Schließzeit bearbeiten')" />
                            </button>
                            <button
                                v-if="closingTime.id && (closingTime._edit || closingTime.deleted)"
                                type="button"
                                class="kgr-table__action"
                                @click="reset(closingTime)"
                            >
                                <kgr-icon-reset :title="$t('Änderungen rückgängig machen')" />
                            </button>
                            <button
                                v-if="!closingTime.deleted"
                                type="button"
                                class="kgr-table__action"
                                @click="remove(closingTime)"
                            >
                                <kgr-icon-delete :title="$t('Schließzeit löschen')" />
                            </button>
                        </td>
                    </tr>
                </tbody>
            </kgr-table>

            <kgr-empty-data v-if="!loading && filteredClosingTimes.length === 0" :value="$t('dataTable.noData')" />
            <br />
            <div class="kgr-postfix kgr-postfix--bigGutters kgr-postfix--center kgr-row">
                <div class="kgr-postfix__fluid"></div>
                <div class="kgr-postfix__fixed kgr-postfix__fixed--noShrink kgr-hidden-print js-test-actionButtonsBlock">
                    <kgr-button
                        icon-add
                        :value="$t('Schließzeit hinzufügen')"
                        @click="add"
                    />
                </div>
            </div>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import ClosingTime from '../../../js/dto/baykibig/ClosingTimes.js';
import DateObjectPicker from '../../../components/common/form/picker/DateObjectPicker.vue';
import EmptyData from '../../../components/common/EmptyData.vue';
import Error from '../../../js/model/Error.js';
import FormScaffold from '../../../components/common/form/FormScaffold.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import IconDelete from '../../../components/common/icon/IconDelete.vue';
import IconEdit from '../../../components/common/icon/IconEdit.vue';
import IconReset from '../../../components/common/icon/IconReset.vue';
import KgrButton from '../../../components/common/form/button/KgrButton.vue';
import KgrDate from '../../../js/date/Date';
import axios from 'axios';

export default {
    components: {
        'kgr-date-picker': DateObjectPicker,
        'kgr-icon-delete': IconDelete,
        'kgr-form-scaffold': FormScaffold,
        'kgr-icon-edit': IconEdit,
        'kgr-icon-reset': IconReset,
        'kgr-generic-select': GenericSelect,
        'kgr-empty-data': EmptyData,
        'kgr-button': KgrButton,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            pending: false,
            start: null,
            end: null,
            closingTimes: [],
            years: [],
            currentYear: null,
            typeOptions: [
                { value: 'default', text: 'Schließzeit' },
                { value: 'team', text: 'Schließzeit Teamfortbildung' },
            ],
        };
    },
    computed: {
        allValid() {
            return this.closingTimes.every((c) => !c._edit || c._edit.error.isEmpty());
        },
        filteredClosingTimes() {
            return this.closingTimes.filter((ct) => {
                if (!this.currentYear) {
                    return false;
                }
                if (ct.id === null) {
                    return true;
                }
                return ct.start.isBeforeOrEqual(this.currentYear.end) && ct.end.isAfterOrEqual(this.currentYear.start);
            });
        },
    },
    mounted() {
        this.loadYears();
        this.loadClosingTimes();
    },
    methods: {
        add() {
            const closingTime = new ClosingTime();
            closingTime._edit = new ClosingTime();
            closingTime._edit.error = new Error();
            this.closingTimes.push(closingTime);
        },
        save() {
            const closingTimesToValidate = this.filteredClosingTimes
                .filter((c) => c._edit || c.deleted);
            closingTimesToValidate.forEach((c) => {
                this.validate(c);
            });
            if (!this.allValid) {
                return;
            }
            const closingTimesToSave = closingTimesToValidate
                .map((c) => (c._edit ? c._edit : c).serialize());
            this.loading = true;

            axios.patch(`/kindergartens/${this.kindergartenId}/baykibig/closing-times`, {
                closingTimes: closingTimesToSave,
            })
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.loadClosingTimes();
                })
                .catch(() => {
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        validate(closingTime) {
            if (closingTime._edit) {
                closingTime._edit.error = new Error();
            }
            this.$forceUpdate();
            if (closingTime.deleted) {
                return;
            }
            if (closingTime._edit.type !== 'default' && closingTime._edit.type !== 'team') {
                closingTime._edit.error.add('type', this.$t('Bitte geben Sie die Art der Schließungszeiten ein'));
            }
            if (!closingTime._edit.start) {
                closingTime._edit.error.add('start', this.$t('Bitte geben Sie ein gültiges Datum ein'));
            }
            if (!closingTime._edit.end) {
                closingTime._edit.error.add('end', this.$t('Bitte geben Sie ein gültiges Datum ein'));
            }
            if (closingTime._edit.start && closingTime._edit.end && closingTime._edit.end.isBefore(closingTime._edit.start)) {
                closingTime._edit.error.add('end', this.$t('Das Enddatum muss nach oder gleich dem Startdatum sein'));
            }
            if (closingTime._edit.start && this.currentYear) {
                if (closingTime._edit.start.isBefore(this.currentYear.start) || closingTime._edit.start.isAfter(this.currentYear.end)) {
                    closingTime._edit.error.add('start', this.$t('Das Startdatum muss innerhalb des ausgewählten Jahres liegen'));
                }
            }
            if (closingTime._edit.end && this.currentYear) {
                if (closingTime._edit.end.isBefore(this.currentYear.start) || closingTime._edit.end.isAfter(this.currentYear.end)) {
                    closingTime._edit.error.add('end', this.$t('Das Enddatum muss innerhalb des ausgewählten Jahres liegen'));
                }
            }
        },
        edit(closingTime) {
            const clone = closingTime.clone();
            clone.error = new Error();
            closingTime._edit = clone;
        },
        reset(closingTime) {
            closingTime._edit = null;
            closingTime.deleted = false;
        },
        remove(closingTime) {
            closingTime._edit = null;
            if (closingTime.id) {
                closingTime.deleted = true;
            } else {
                this.closingTimes = this.closingTimes.filter((c) => c !== closingTime);
            }
        },
        loadYears() {
            axios.get('/baykibig/years')
                .then((response) => {
                    this.years = response.data.data.map((y) => ({
                        id: y.id,
                        start: KgrDate.fromIso8601(y.start),
                        end: KgrDate.fromIso8601(y.end),
                        title: y.title,
                    }));
                    this.currentYear = this.years[0];
                })
                .catch(() => {
                    this.$notify.error('error.loadingData');
                });
        },
        changeYear(year) {
            this.resetAllChanges();
            this.currentYear = year;
        },
        resetAllChanges() {
            this.closingTimes.forEach((ct) => {
                this.reset(ct);
            });

            this.closingTimes = this.closingTimes.filter((ct) => ct.id !== null);
        },
        loadClosingTimes() {
            this.loading = true;
            axios.get(`/kindergartens/${this.kindergartenId}/baykibig/closing-times`)
                .then((response) => {
                    this.closingTimes = response.data.data.map((ct) => {
                        const closingTime = new ClosingTime();
                        closingTime.id = ct.id;
                        closingTime.type = ct.type;
                        closingTime.start = KgrDate.fromIso8601(ct.start);
                        closingTime.end = KgrDate.fromIso8601(ct.end);
                        return closingTime;
                    });
                })
                .catch(() => {
                    this.$notify.error('error.loadingData');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped>
tr.deleted td {
    opacity: 0.5;
    text-decoration: line-through;
}
tr.deleted td.kgr-table__cell--actions {
    opacity: 1;
    text-decoration: none;
}
</style>
